import createClient from 'openapi-fetch';

import type { paths } from './schema.js';

type ErrorEnvelope = {
  error?: {
    code?: unknown;
    message?: unknown;
    retryable?: unknown;
    action_required?: unknown;
    details?: unknown;
  };
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryable: boolean;
  readonly actionRequired: string | null;
  readonly details: Readonly<Record<string, unknown>>;

  constructor(
    status: number,
    options: {
      message: string;
      code?: string;
      retryable?: boolean;
      actionRequired?: string | null;
      details?: Readonly<Record<string, unknown>>;
    },
  ) {
    super(options.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options.code ?? 'request_failed';
    this.retryable = options.retryable ?? status >= 500;
    this.actionRequired = options.actionRequired ?? null;
    this.details = options.details ?? {};
  }

  static fromResponse(response: Response, payload: unknown): ApiError {
    const envelope = isRecord(payload) ? (payload as ErrorEnvelope).error : undefined;
    const details = isRecord(envelope?.details) ? envelope.details : {};
    const code = typeof envelope?.code === 'string' ? envelope.code : null;
    return new ApiError(response.status, {
      ...(code === null ? {} : { code }),
      message:
        typeof envelope?.message === 'string'
          ? envelope.message
          : `Request failed (${response.status})`,
      retryable:
        typeof envelope?.retryable === 'boolean' ? envelope.retryable : response.status >= 500,
      actionRequired:
        typeof envelope?.action_required === 'string' ? envelope.action_required : null,
      details,
    });
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeApiBaseUrl(value: string | undefined) {
  return value?.trim().replace(/\/+$/, '') ?? '';
}

export function resolveApiUrl(path: string, baseUrl = API_BASE_URL) {
  if (!baseUrl) return path;
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export const apiClient = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: 'include',
});

export async function unwrap<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const result = await request;
  if (result.data !== undefined) return result.data;
  throw ApiError.fromResponse(result.response, result.error);
}

export async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(resolveApiUrl(path), { credentials: 'include', ...init });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) throw ApiError.fromResponse(response, payload);
  return payload as T;
}
